import { Redacted, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import {
  type PathfinderSignAndSendResponse,
  type SignAndSendPlainTxPayloadDto,
  type SignPlainTxPayloadDto,
  type SignPermitPayloadDto,
  type SignTransactionPayloadDto,
  type SignTransactionResponseDto,
} from '@root/features/secure-storage/secure-storage-service'

import { SecureStorageErrorCode } from '../../../shared/ui/secure-storage-error-code'
import { WrongPasswordError } from '../secure-storage-error'

import { safeInvoke } from './tauri-secure-storage-util'

import type {
  BuildAndSignSwapTransactionPayloadDto,
  BuildSignAndSendSwapTransactionPayloadDto,
  BuildSignAndSendResponseDto,
  BulkBuildSignAndSendSwapTransactionResponseDto,
} from '@root/features/secure-storage/secure-storage-service'

/**
 * If operation is swap, password can be empty string
 */
export async function signTransaction(payload: SignTransactionPayloadDto) {
  return (
    await safeInvoke<SignTransactionResponseDto>(`sign_transaction`, {
      payload: signTransactionPayloadToDto(payload),
    })
  ).pipe(
    Result.map((response) => response),
    Result.mapError((err) => {
      if (err.cause === SecureStorageErrorCode.WrongPassword) {
        return new WrongPasswordError()
      }
      return err
    }),
  )
}

const signTransactionPayloadToDto = (payload: SignTransactionPayloadDto) => {
  return {
    walletId: payload.walletId,
    password:
      payload.password === undefined
        ? undefined
        : Redacted.value(payload.password),
    network: Network.toSupportedNetwork(payload.network),
    tx: payload.tx,
    operation: payload.operation,
    feeSource: payload.feeSource,
    amount: payload.amount,
  }
}

/**
 * If operation is swap, password can be empty string
 */
export async function signTransactions(
  transactions: Array<SignTransactionPayloadDto>,
) {
  const result = await safeInvoke<Array<SignTransactionResponseDto>>(
    `sign_transactions`,
    {
      payload: transactions.map(signTransactionPayloadToDto),
    },
  )

  return result
}

export async function buildAndSignSwapTransaction(
  params: BuildAndSignSwapTransactionPayloadDto,
) {
  return safeInvoke<SignTransactionResponseDto>(
    'build_and_sign_swap_transaction',
    { params },
  )
}

export async function buildSignAndSendSwapTransaction(
  params: BuildSignAndSendSwapTransactionPayloadDto,
) {
  return safeInvoke<BuildSignAndSendResponseDto>(
    'build_sign_and_send_swap_transaction',
    { params },
  )
}
export async function bulkBuildSignAndSendSwapTransaction(
  params: Array<BuildSignAndSendSwapTransactionPayloadDto>,
) {
  return safeInvoke<Array<BulkBuildSignAndSendSwapTransactionResponseDto>>(
    'bulk_build_sign_and_send_swap_transaction',
    { params },
  )
}

export async function signPlainTransaction(payload: SignPlainTxPayloadDto) {
  return safeInvoke<SignTransactionResponseDto>(`sign_plain_transaction`, {
    payload: {
      walletId: payload.walletId,
      network: Network.toSupportedNetwork(payload.network),
      tx: payload.tx,
    },
  })
}

export async function signAndSendPlainTransaction(
  payload: SignAndSendPlainTxPayloadDto,
) {
  return safeInvoke<PathfinderSignAndSendResponse>(
    `sign_and_send_plain_transaction`,
    {
      params: {
        walletId: payload.walletId,
        network: Network.toSupportedNetwork(payload.network),
        tx: payload.tx,
        sender: payload.sender,
      },
    },
  )
}

export const signPermit = async (payload: SignPermitPayloadDto) => {
  const result = await safeInvoke<{ signature: string }>(`sign_permit`, {
    payload,
  })

  return result
}
